/**
 * These materials are used to render hatch textures.
 */
import { hexToRgb } from "common/utils/colorConversion";
import stableHash from "common/utils/stableHash";
import { aabb2New } from "geom/aabb2";
import { fillHatchLines2D } from "geom/hatch";
import { HatchMaterialType, type HatchMaterial } from "geom/hatchTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import { copyHatchDesignMaterialParams, hatchMaterialFromDesignMaterial } from "gfx/hatchMaterialFromDesignMaterial";
import { getLibraryHatchMaterialSize } from "gfx/hatchMaterialLoader";
import * as THREE from "three";
import initSDFGenerator from "webgl-sdf-generator";
import { UVTransformVertexShader } from "../threejs/higharc/UVVertexShader";
import type { DesignMaterialParamsPreprocessed } from "./designMaterialTypes";
import { hatchDistanceScaleFunction } from "./hatchMaterial";

const DEFAULT_HATCH_SIZE = 32;
// Sets the resolution of the SDF texture.
const SDF_SIZE = 256;
// TODO: use the same hatch color as in PDF.
const HATCH_COLOR = "#909090";
// Controls the thickness of the hatch lines.
export const DEFAULT_HATCH_THRESHOLD = 0.5;
export const BIGGER_HATCH_THRESHOLD = 1.0;

const hatchTextureMaterialCache: { [cacheKey: string]: THREE.ShaderMaterial } = {};
const sdfGenerator = initSDFGenerator();

function getHatchMaterialSize(hatchMaterial: HatchMaterial): { sizeU: number; sizeV: number } {
  if (hatchMaterial.type === HatchMaterialType.DESIGN) {
    return hatchMaterial;
  } else if (hatchMaterial.type === HatchMaterialType.PROCEDURAL) {
    return getLibraryHatchMaterialSize(hatchMaterial.materialId);
  } else {
    return { sizeU: DEFAULT_HATCH_SIZE, sizeV: DEFAULT_HATCH_SIZE };
  }
}

function linesToSVGPathCommands(lines: PolyLine2[]): string {
  let s = "";
  for (let i = 0, l = lines.length; i < l; i++) {
    const [x0, y0, x1, y1] = lines[i];
    s += "M" + x0 + "," + y0;
    s += "L" + x1 + "," + y1;
  }
  return s;
}

const fragmentShader = /* glsl */ `
varying vec2 vUv;

${hatchDistanceScaleFunction}

uniform sampler2D tSdf;
uniform vec3 color;
uniform vec2 texSize;
uniform float maxDist;
uniform float threshold;

const float minDelta = 0.13;

void main() {
  float texValue = texture2D(tSdf, vUv).r;

  float delta = getHatchDistanceScale(vUv, texSize, minDelta);

  // The SDF texture uses 0.5 to represent the implicit surface boundary.
  float absDist = abs(texValue * 2.0 - 1.0) * maxDist;
  float scaledDist = absDist / delta;
  float alpha = scaledDist <= threshold ? 1.0 : 0.0;
  gl_FragColor = vec4(color, alpha);
}
`;

/**
 * Generates a SDF shader material for a hatch material.
 *
 * @param hatchMaterial The hatch material for the design material.
 * @returns THREE.Material that renders the hatch material from an SDF.
 */
export function createThreeMaterialFromHatchMaterial(
  hatchMaterial: HatchMaterial,
  hatchThreshold: number,
): THREE.ShaderMaterial {
  const { sizeU, sizeV } = getHatchMaterialSize(hatchMaterial);

  const [sdfU, sdfV] = [SDF_SIZE, SDF_SIZE];

  const aabb2 = aabb2New(-0.5 * sizeU, -0.5 * sizeV, 1.5 * sizeU, 1.5 * sizeV);
  const lines = fillHatchLines2D(hatchMaterial, aabb2);

  const path = linesToSVGPathCommands(lines);

  const maxDist = Math.max(sizeU, sizeV) * 0.5;

  const sdfImageData = sdfGenerator.webgl.generate(
    sdfU,
    sdfV,
    path,
    [0, 0, sizeU, sizeV],
    maxDist,
  ) as Uint8Array<ArrayBuffer>;

  type DataTextureConstructorArgs = ConstructorParameters<typeof THREE.DataTexture>;
  const dataTextureArgs: DataTextureConstructorArgs = [
    sdfImageData,
    sdfU,
    sdfV,
    THREE.RedFormat,
    THREE.UnsignedByteType,
    THREE.UVMapping,
    THREE.RepeatWrapping,
    THREE.RepeatWrapping,
    THREE.LinearFilter,
    THREE.LinearFilter,
  ];

  const dataTexture = new THREE.DataTexture(...dataTextureArgs);

  // For debugging, you can save the SDFs by uncommenting this code.
  // if (svgString !== "Z") {
  //   dataTexture.matrixAutoUpdate = true;
  //   dataTexture.repeat = new THREE.Vector2(1, 1);
  //   dataTexture.needsUpdate = true;
  //   dataTexture.updateMatrix();

  //   const renderer = new THREE.WebGLRenderer();
  //   renderer.setSize(sdfU, sdfV);
  //   document.body.appendChild(renderer.domElement);

  //   const scene = new THREE.Scene();
  //   scene.add(new THREE.AmbientLight("rgb(255,255,255)", 1));

  //   scene.background = dataTexture;

  //   const camera = new THREE.PerspectiveCamera();
  //   renderer.render(scene, camera);

  //   const imageURL = renderer.domElement.toDataURL("image/png");
  //   var anchor = document.createElement("a");
  //   anchor.href = imageURL;
  //   anchor.download = "hatch.png";
  //   anchor.click();
  // }

  dataTexture.matrixAutoUpdate = true;
  dataTexture.repeat = new THREE.Vector2(1 / sizeU, 1 / sizeV);
  dataTexture.needsUpdate = true;
  dataTexture.updateMatrix();

  const colorRgb = hexToRgb(HATCH_COLOR);
  const colorVec = new THREE.Vector3(colorRgb[0] / 255, colorRgb[1] / 255, colorRgb[2] / 255);

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uvTransform: { value: dataTexture.matrix },
      tSdf: { value: dataTexture },
      color: { value: colorVec },
      texSize: { value: new THREE.Vector2(sizeU, sizeV) },
      maxDist: { value: maxDist },
      threshold: { value: hatchThreshold },
    },
    vertexShader: UVTransformVertexShader,
    fragmentShader,
    fog: false,
    depthTest: true,
    depthWrite: true,
    blending: THREE.NoBlending,
    transparent: true,
  });
  return material;
}

/**
 * @returns material
 */
export function getHatchTextureMaterial(
  designMaterialParams: DesignMaterialParamsPreprocessed,
  hatchThreshold,
  libraryHatchMaterial?: HatchMaterial,
): THREE.ShaderMaterial {
  const hatchMaterialDesignParams = copyHatchDesignMaterialParams(designMaterialParams);
  const cacheKey = stableHash({ ...hatchMaterialDesignParams, hatchThreshold });
  if (hatchTextureMaterialCache.hasOwnProperty(cacheKey)) {
    return hatchTextureMaterialCache[cacheKey];
  }

  const hatchMaterial = libraryHatchMaterial ?? hatchMaterialFromDesignMaterial(hatchMaterialDesignParams, true);
  const threeMaterial = createThreeMaterialFromHatchMaterial(hatchMaterial, hatchThreshold);

  hatchTextureMaterialCache[cacheKey] = threeMaterial;
  return threeMaterial;
}
